import React, { useEffect, useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Chip,
    CircularProgress,
    Button,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Paper,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { MENU_ITEM_KEY_PREFIX, MENU_CATEGORY_KEY_PREFIX } from '../../assests/Constants/menuRestrictionTranslationKeys.js';
import { toast } from 'react-toastify';
import PermissionService from '../../Services/permission.service.js';
import SubAccountantCaseRestrictionsEditor from './SubAccountantFormModal/SubAccountantCaseRestrictionsEditor.jsx';
import { MENU_ITEMS } from '../../assests/Constants/menuRestrictionKeys.js';

/** Legacy key that was used for the dashboard; normalize to AccountCardDashboard so save sends the correct key. */
const LEGACY_DASHBOARD_KEY = 'DebtorsCreditorsLedgerReport';
const DASHBOARD_KEY = MENU_ITEMS.AccountCardDashboard;

const normalizeRestrictionKeys = (keys) => {
    if (!Array.isArray(keys)) return [];
    const normalized = keys.map((k) => (k === LEGACY_DASHBOARD_KEY ? DASHBOARD_KEY : k));
    return [...new Set(normalized)];
};

const getRestrictionLabel = (t, key) => {
    if (!key) return key;
    const itemLabel = t(`${MENU_ITEM_KEY_PREFIX}.${key}`);
    if (itemLabel !== `${MENU_ITEM_KEY_PREFIX}.${key}`) return itemLabel;
    const catLabel = t(`${MENU_CATEGORY_KEY_PREFIX}.${key}`);
    if (catLabel !== `${MENU_CATEGORY_KEY_PREFIX}.${key}`) return catLabel;
    return key;
};

const normSubId = (id) => {
    if (id == null || id === '') return '';
    return String(id).trim().toLowerCase();
};

const getClientSubAccountantId = (c) => c?.subAccountantId ?? c?.SubAccountantId ?? '';

const clientIsAssignedToSub = (c, subAccountantId) => {
    if (!subAccountantId) return false;
    return normSubId(getClientSubAccountantId(c)) === normSubId(subAccountantId);
};

const clientCaseIdString = (c) => String(c?.caseId ?? c?.id ?? '');

/** Keys denied for every selected case (intersection); used when editing multiple clients at once. */
const intersectionRestrictionKeysForCases = (grouped, caseIds) => {
    const ids = (caseIds || []).map((id) => String(id));
    if (ids.length === 0) return [];
    const lists = ids.map((id) => normalizeRestrictionKeys(grouped[id] || []));
    return lists.reduce((acc, keys) => acc.filter((k) => keys.includes(k)), lists[0]);
};

export default function SubAccountantRestrictionsViewModal({
    open,
    onClose,
    subAccountantId,
    subAccountantName,
    token,
    clientsList = [],
    clientsLoading = false,
    t,
    direction,
    onSaveSuccess,
}) {
    const [grouped, setGrouped] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [restrictionScope, setRestrictionScope] = useState('all');
    /** caseIds selected in the client list (multi-client restrictions) */
    const [selectedCaseIds, setSelectedCaseIds] = useState([]);
    const [restrictionKeys, setRestrictionKeys] = useState([]);
    /** Filter client picker and restriction summary: all | assigned to this sub-accountant | everyone else */
    const [clientsListFilter, setClientsListFilter] = useState('all');

    const filteredClientsForPicker = useMemo(() => {
        const list = clientsList || [];
        if (!subAccountantId || clientsListFilter === 'all') return list;
        if (clientsListFilter === 'associated') {
            return list.filter((c) => clientIsAssignedToSub(c, subAccountantId));
        }
        return list.filter((c) => !clientIsAssignedToSub(c, subAccountantId));
    }, [clientsList, subAccountantId, clientsListFilter]);

    // Sync restrictionKeys from grouped: global scope vs selected client(s); multi = intersection of denied keys.
    useEffect(() => {
        if (!open || !subAccountantId) return;
        if (restrictionScope === 'all') {
            const raw = Array.isArray(grouped['']) ? grouped[''] : [];
            setRestrictionKeys(normalizeRestrictionKeys(raw));
            return;
        }
        if (selectedCaseIds.length === 0) {
            setRestrictionKeys([]);
            return;
        }
        if (selectedCaseIds.length === 1) {
            const id = selectedCaseIds[0];
            const raw = Array.isArray(grouped[id]) ? grouped[id] : [];
            setRestrictionKeys(normalizeRestrictionKeys(raw));
            return;
        }
        setRestrictionKeys(intersectionRestrictionKeysForCases(grouped, selectedCaseIds));
    }, [open, subAccountantId, restrictionScope, selectedCaseIds, grouped]);

    const fetchGrouped = () => {
        if (!subAccountantId || !token) return;
        setLoading(true);
        PermissionService.GetRestrictionsBySubAccountantGroupedByCase(subAccountantId, token)
            .then((data) => setGrouped(data || {}))
            .catch(() => setGrouped({}))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!open || !subAccountantId || !token) {
            setGrouped({});
            return;
        }
        fetchGrouped();
    }, [open, subAccountantId, token]);

    useEffect(() => {
        if (!open || !subAccountantId) return;
        setRestrictionScope('all');
        setSelectedCaseIds([]);
        setClientsListFilter('all');
    }, [open, subAccountantId]);

    useEffect(() => {
        const allowed = new Set(
            (filteredClientsForPicker || []).map((c) => clientCaseIdString(c))
        );
        setSelectedCaseIds((prev) => prev.filter((id) => allowed.has(String(id))));
    }, [filteredClientsForPicker]);

    const toggleCaseSelected = (caseId) => {
        const id = String(caseId);
        setSelectedCaseIds((prev) => {
            const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
            if (next.length > 0) setRestrictionScope('clients');
            return next;
        });
    };

    const selectAllFilteredClients = () => {
        const ids = (filteredClientsForPicker || []).map((c) => clientCaseIdString(c));
        setSelectedCaseIds(ids);
        if (ids.length > 0) setRestrictionScope('clients');
    };

    const clearClientSelection = () => setSelectedCaseIds([]);

    const handleRestrictionScopeChange = (value) => {
        setRestrictionScope(value);
        if (value === 'all') setSelectedCaseIds([]);
    };

    const clientLabel = (caseId) => {
        if (caseId === '' || caseId == null) return t('AllClients') || 'All clients';
        const client = (clientsList || []).find(
            (c) => String(c.caseId ?? c.id ?? '') === String(caseId)
        );
        return client?.name ?? client?.clientName ?? caseId;
    };

    const filteredEntries = useMemo(() => {
        const base = Object.entries(grouped).filter(([, keys]) => Array.isArray(keys) && keys.length > 0);
        if (!subAccountantId || clientsListFilter === 'all') return base;
        return base.filter(([caseId]) => {
            if (caseId === '' || caseId == null) return true;
            const client = (clientsList || []).find(
                (c) => String(c.caseId ?? c.id ?? '') === String(caseId)
            );
            const assignedHere = client ? clientIsAssignedToSub(client, subAccountantId) : false;
            if (clientsListFilter === 'associated') return assignedHere;
            return !assignedHere;
        });
    }, [grouped, clientsList, subAccountantId, clientsListFilter]);

    const hasAnyGroupedRestrictions = useMemo(
        () => Object.values(grouped).some((keys) => Array.isArray(keys) && keys.length > 0),
        [grouped]
    );

    const buildRestrictionsArray = async () => {
        const keyDefinitions = await PermissionService.GetRestrictionKeyDefinitions(token);
        const rawKeySet = new Set(restrictionKeys || []);
        return (keyDefinitions || []).map((def) => {
            const key = def.key;
            let isDenied = rawKeySet.has(key);
            if (key === DASHBOARD_KEY) {
                isDenied = rawKeySet.has(DASHBOARD_KEY) || rawKeySet.has(LEGACY_DASHBOARD_KEY);
            } else if (key === LEGACY_DASHBOARD_KEY) {
                isDenied = false;
            }
            return { restrictionKey: key, isDenied };
        });
    };

    const handleSaveRestrictions = async () => {
        if (!subAccountantId || !token) return;
        if (restrictionScope === 'clients' && selectedCaseIds.length === 0) {
            toast.warning(t('RestrictionsSelectAtLeastOneClient') || 'Select at least one client from the list.');
            return;
        }
        setSaving(true);
        try {
            const restrictions = await buildRestrictionsArray();
            if (restrictionScope === 'all') {
                await PermissionService.BulkSetSubAccountantRestrictions(
                    { subAccountantId, caseId: null, restrictions },
                    token
                );
            } else {
                await PermissionService.BulkSetSubAccountantRestrictions(
                    { subAccountantId, caseIds: selectedCaseIds, restrictions },
                    token
                );
            }
            toast.success(t('RestrictionsSaved') || t('Saved') || 'Saved');
            fetchGrouped();
            onSaveSuccess?.();
        } catch (err) {
            console.error('Failed to save restrictions', err);
            toast.error(t('FailedToSaveRestrictions') || 'Failed to save menu restrictions');
        }
        setSaving(false);
    };

    return (
        <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth dir={direction}>
            <DialogTitle>
                {t('ViewRestrictions') || 'View restrictions'}
                {subAccountantName ? ` — ${subAccountantName}` : ''}
            </DialogTitle>
            <DialogContent>
                {subAccountantId && (
                    <Box sx={{ mb: 2 }} dir="rtl">
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 600 }}>
                            {t('RestrictionsClientsListFilter') || 'Clients'}
                        </Typography>
                        <ToggleButtonGroup
                            value={clientsListFilter}
                            exclusive
                            onChange={(_, value) => {
                                if (value != null) setClientsListFilter(value);
                            }}
                            size="small"
                            color="primary"
                            sx={{ flexWrap: 'wrap' }}
                        >
                            <ToggleButton value="all">
                                {t('RestrictionsClientsFilterAll') || 'All clients'}
                            </ToggleButton>
                            <ToggleButton value="associated">
                                {t('RestrictionsClientsFilterAssociated') || 'Assigned to this manager'}
                            </ToggleButton>
                            <ToggleButton value="other">
                                {t('RestrictionsClientsFilterOther') || 'Not assigned to this manager'}
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                            {t('RestrictionsClientsListCaption') ||
                                'Clients in this list match the filter. Select clients to edit restrictions for several at once.'}
                        </Typography>
                    </Box>
                )}
                {subAccountantId && (
                    <Paper
                        variant="outlined"
                        sx={{
                            mb: 2,
                            maxHeight: 260,
                            overflow: 'auto',
                            p: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                mb: 0.5,
                                position: 'sticky',
                                top: 0,
                                bgcolor: 'background.paper',
                                zIndex: 1,
                                py: 0.5,
                            }}
                        >
                            <Typography variant="body2" fontWeight={600}>
                                {t('RestrictionsClientsInList', { count: filteredClientsForPicker.length }) ||
                                    `Clients (${filteredClientsForPicker.length})`}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <Button size="small" onClick={selectAllFilteredClients} disabled={clientsLoading || filteredClientsForPicker.length === 0}>
                                    {t('RestrictionsSelectAllVisible') || 'Select all'}
                                </Button>
                                <Button size="small" onClick={clearClientSelection} disabled={selectedCaseIds.length === 0}>
                                    {t('RestrictionsClearSelection') || 'Clear'}
                                </Button>
                            </Box>
                        </Box>
                        {clientsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : filteredClientsForPicker.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                {t('RestrictionsEmptyFilteredClientList') || 'No clients match this filter.'}
                            </Typography>
                        ) : (
                            filteredClientsForPicker.map((c) => {
                                const id = clientCaseIdString(c);
                                const label = c?.name ?? c?.clientName ?? id;
                                const checked = selectedCaseIds.includes(id);
                                return (
                                    <FormControlLabel
                                        key={id}
                                        sx={{ display: 'flex', ml: 0, mr: 0, mb: 0.25, alignItems: 'flex-start' }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={checked}
                                                onChange={() => toggleCaseSelected(id)}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" component="span">
                                                {label}
                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    {id}
                                                </Typography>
                                            </Typography>
                                        }
                                    />
                                );
                            })
                        )}
                    </Paper>
                )}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : filteredEntries.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                        {!hasAnyGroupedRestrictions
                            ? (t('NoRestrictions') || 'No restrictions')
                            : (t('RestrictionsNoClientsInFilter') ||
                                'No restrictions match this client filter. Try another filter or add restrictions below.')}
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0 }}>
                        {filteredEntries.map(([caseId, keys]) => (
                            <Box
                                key={caseId === '' ? '_global' : caseId}
                                sx={{
                                    p: 1.5,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: 'grey.50',
                                }}
                            >
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75 }}>
                                    {clientLabel(caseId)}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {keys.map((key) => (
                                        <Chip
                                            key={key}
                                            label={getRestrictionLabel(t, key)}
                                            size="small"
                                            icon={<LockIcon sx={{ fontSize: 14 }} />}
                                            variant="outlined"
                                            sx={{ fontSize: '11px', height: 24 }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                {subAccountantId && token && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                            {t('SubAccountantCaseRestrictions') || 'Add / remove restrictions'}
                        </Typography>
                        <SubAccountantCaseRestrictionsEditor
                            subAccountantId={subAccountantId}
                            token={token}
                            restrictionScope={restrictionScope}
                            setRestrictionScope={handleRestrictionScopeChange}
                            selectedClientsCount={selectedCaseIds.length}
                            restrictionKeys={restrictionKeys}
                            onRestrictionKeysChange={setRestrictionKeys}
                            t={t}
                            loadRestrictionsFromParent
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
                <Button onClick={onClose}>{t('close') || 'Close'}</Button>
                {subAccountantId && token && (
                    <Button
                        variant="contained"
                        onClick={handleSaveRestrictions}
                        disabled={saving || (restrictionScope === 'clients' && selectedCaseIds.length === 0)}
                    >
                        {saving ? (t('Saving') || 'Saving...') : (t('save') || 'Save')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
